/**
 * TkSearchSpell — typo-correction layer for the TK predictive search.
 *
 * Shopify's predictive search only tolerates 1 wrong letter AND requires the
 * first 4 letters of a term to be typed correctly, so common misspellings
 * ("chiken", "slami", "shnitzel", "cuscus") return nothing. This module builds
 * a vocabulary from the store's own catalog (via the search-index-tk section)
 * and suggests a corrected query when a search comes back without products.
 *
 * Used by assets/tk-search-engine.js (the shared fetch pipeline behind both
 * search surfaces), which calls correct() when a query returns no products.
 * The surfaces themselves call warm() directly on focus/open:
 *   - blocks/ai_gen_block_3c36854.liquid (hero search bar)
 *   - snippets/tk-search-overlay.liquid  (site-wide search overlay)
 *
 * API:
 *   window.TkSearchSpell.warm()          prefetch the vocabulary (call on focus/open)
 *   window.TkSearchSpell.correct(query)  Promise<{ query: string, changed: boolean }>
 *                                        never rejects — falls back to { query, changed: false }
 */
(function () {
  'use strict';

  if (window.TkSearchSpell) return;

  var INDEX_SECTION_ID = 'search-index-tk';
  var MAX_INDEX_PAGES = 8; // 8 pages x 250 products — safety cap
  var CACHE_KEY = 'tk-search-vocab-v1';
  var CACHE_TTL_MS = 30 * 60 * 1000;
  var MIN_WORD_LENGTH = 3; // never correct 1-2 letter words

  var root =
    (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  var vocabPromise = null;
  var memo = Object.create(null);

  /* ---------- text helpers ---------- */

  function normalize(str) {
    str = String(str).toLowerCase();
    if (str.normalize) {
      // Strip diacritics: "café" -> "cafe"
      str = str.normalize('NFKD').replace(/[\u0300-\u036f]/g, '');
    }
    return str
      .replace(/[\u2018\u2019']/g, '') // "lieber's" -> "liebers"
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();
  }

  function tokenize(str) {
    var norm = normalize(str);
    return norm ? norm.split(' ') : [];
  }

  function freq(vocab, word) {
    return Object.prototype.hasOwnProperty.call(vocab, word) ? vocab[word] : 0;
  }

  /* ---------- edit distance ---------- */

  /**
   * Restricted Damerau-Levenshtein (optimal string alignment) with a cutoff:
   * counts insertions, deletions, substitutions and adjacent transpositions,
   * returning max + 1 as soon as the distance is known to exceed `max`.
   *
   * With prefix = true, returns the distance from `a` to the closest prefix
   * of `b` — used for the word the shopper is still typing ("chik" is 1 edit
   * from "chic", the 4-letter prefix of "chicken").
   */
  function editDistance(a, b, max, prefix) {
    var la = a.length;
    var lb = b.length;
    if (!prefix && Math.abs(la - lb) > max) return max + 1;
    if (prefix && lb < la - max) return max + 1;

    var prevPrev = null;
    var prev = [];
    var i, j;
    for (j = 0; j <= lb; j++) prev[j] = j;
    var prevMin = 0;

    for (i = 1; i <= la; i++) {
      var cur = [i];
      var rowMin = i;
      var ca = a.charCodeAt(i - 1);
      for (j = 1; j <= lb; j++) {
        var cost = ca === b.charCodeAt(j - 1) ? 0 : 1;
        var v = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + cost);
        if (
          prevPrev !== null &&
          j > 1 &&
          ca === b.charCodeAt(j - 2) &&
          a.charCodeAt(i - 2) === b.charCodeAt(j - 1)
        ) {
          var t = prevPrev[j - 2] + 1;
          if (t < v) v = t;
        }
        cur[j] = v;
        if (v < rowMin) rowMin = v;
      }
      // Row minima can only stall for one row (transpositions read 2 rows
      // back), so two consecutive rows above the cutoff mean we're done.
      if (rowMin > max && prevMin > max) return max + 1;
      prevPrev = prev;
      prev = cur;
      prevMin = rowMin;
    }

    if (prefix) {
      var best = prev[0];
      for (j = 1; j <= lb; j++) {
        if (prev[j] < best) best = prev[j];
      }
      return best;
    }
    return prev[lb];
  }

  /* ---------- correction ---------- */

  /**
   * Returns the corrected form of `word`, or null when no (better) match
   * exists. `isLast` marks the final query token — the one the shopper may
   * still be typing — which enables prefix-aware matching.
   */
  function correctToken(word, vocab, isLast) {
    if (word.length < MIN_WORD_LENGTH || freq(vocab, word) > 0) return null;

    var t;
    if (isLast) {
      // Mid-typing: if the word is already a live prefix of a catalog word,
      // predictive search matches it on its own — nothing to fix.
      for (t in vocab) {
        if (t.length > word.length && t.indexOf(word) === 0) return null;
      }
    }

    var max = word.length <= 5 ? 1 : 2;
    var best = null;
    var bestD = max + 1;
    var bestPenalty = 2;
    var bestFreq = -1;

    for (t in vocab) {
      if (t.length < MIN_WORD_LENGTH) continue;
      var d = editDistance(word, t, max, false);
      if (isLast && d > 0) {
        var pd = editDistance(word, t, max, true);
        if (pd < d) d = pd;
      }
      if (d > max) continue;
      // Rank by distance, then prefer candidates that keep the shopper's
      // first letter (misspellings rarely change it), then by how common the
      // word is in the catalog.
      var penalty = t.charCodeAt(0) === word.charCodeAt(0) ? 0 : 1;
      if (
        d < bestD ||
        (d === bestD && penalty < bestPenalty) ||
        (d === bestD && penalty === bestPenalty && vocab[t] > bestFreq)
      ) {
        best = t;
        bestD = d;
        bestPenalty = penalty;
        bestFreq = vocab[t];
      }
    }
    if (best) return best;

    // Joined words: "ribsteak" -> "rib steak"
    if (word.length >= 5) {
      for (var i = 2; i <= word.length - 2; i++) {
        var head = word.slice(0, i);
        var tail = word.slice(i);
        if (freq(vocab, head) > 0 && freq(vocab, tail) > 0) {
          return head + ' ' + tail;
        }
      }
    }
    return null;
  }

  /* ---------- vocabulary loading ---------- */

  function addTokens(vocab, str) {
    var toks = tokenize(str);
    for (var i = 0; i < toks.length; i++) {
      var t = toks[i];
      if (t.length < 2) continue;
      if (/^\d+$/.test(t)) continue; // bare numbers (weights, counts) are noise
      vocab[t] = freq(vocab, t) + 1;
    }
  }

  function buildVocab(strings) {
    var vocab = Object.create(null);
    for (var i = 0; i < strings.length; i++) addTokens(vocab, strings[i]);
    return vocab;
  }

  function parseIndexPayload(html) {
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var el = doc.querySelector('[data-tk-search-index]');
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return null;
    }
  }

  function fetchIndexPage(page) {
    var url = root + '?section_id=' + INDEX_SECTION_ID + '&page=' + page;
    return fetch(url)
      .then(function (res) {
        if (!res.ok) throw new Error('tk-search-spell: index request failed');
        return res.text();
      })
      .then(parseIndexPayload);
  }

  function readCache() {
    try {
      var raw = sessionStorage.getItem(CACHE_KEY);
      if (!raw) return null;
      var data = JSON.parse(raw);
      if (!data || !data.vocab || Date.now() - data.t > CACHE_TTL_MS) return null;
      // Re-key onto a null prototype so words like "constructor" stay safe.
      var vocab = Object.create(null);
      for (var k in data.vocab) {
        if (Object.prototype.hasOwnProperty.call(data.vocab, k)) vocab[k] = data.vocab[k];
      }
      return vocab;
    } catch (e) {
      return null;
    }
  }

  function writeCache(vocab) {
    try {
      sessionStorage.setItem(CACHE_KEY, JSON.stringify({ t: Date.now(), vocab: vocab }));
    } catch (e) {
      /* private mode / quota — fine, we just refetch next page load */
    }
  }

  function loadVocab() {
    if (vocabPromise) return vocabPromise;
    var cached = readCache();
    if (cached) {
      vocabPromise = Promise.resolve(cached);
      return vocabPromise;
    }
    vocabPromise = fetchIndexPage(1).then(function (first) {
      if (!first || !first.titles) throw new Error('tk-search-spell: bad index payload');
      var pages = Math.min(first.pages || 1, MAX_INDEX_PAGES);
      var requests = [];
      for (var p = 2; p <= pages; p++) requests.push(fetchIndexPage(p));
      return Promise.all(requests).then(function (rest) {
        var strings = [].concat(first.titles, first.types || [], first.vendors || []);
        for (var i = 0; i < rest.length; i++) {
          if (rest[i] && rest[i].titles) strings = strings.concat(rest[i].titles);
        }
        var vocab = buildVocab(strings);
        writeCache(vocab);
        return vocab;
      });
    });
    vocabPromise.catch(function () {
      vocabPromise = null; // allow a retry on the next call
    });
    return vocabPromise;
  }

  /* ---------- public API ---------- */

  function correct(query) {
    return loadVocab()
      .then(function (vocab) {
        var tokens = tokenize(query);
        if (!tokens.length) return { query: query, changed: false };
        var key = tokens.join(' ');
        if (memo[key]) return memo[key];

        var changed = false;
        var out = [];
        for (var i = 0; i < tokens.length; i++) {
          var fixed = correctToken(tokens[i], vocab, i === tokens.length - 1);
          if (fixed && fixed !== tokens[i]) {
            changed = true;
            out.push(fixed);
          } else {
            out.push(tokens[i]);
          }
        }

        var result = { query: out.join(' '), changed: changed };
        memo[key] = result;
        return result;
      })
      .catch(function () {
        return { query: query, changed: false };
      });
  }

  window.TkSearchSpell = {
    warm: function () {
      loadVocab().catch(function () {});
    },
    correct: correct,
    // Exposed for testing/debugging only.
    _internals: {
      normalize: normalize,
      tokenize: tokenize,
      editDistance: editDistance,
      buildVocab: buildVocab,
      correctToken: correctToken
    }
  };
})();
